import { Box, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getSignupFunnel } from "../../../api/region";
import { setLoading } from "../../user-container/reducer/userSlice";

const UserFlowBreakdown = () => {
	const filter = useSelector((state) => state.filter);
	const [isLoading, setIsLoading] = useState(false);
	const filterWithoutUTM = useMemo(() => {
		const { utm_options, ...rest } = filter;
		return rest;
	}, [JSON.stringify(filter)]);

	const [totals, setTotals] = useState({
		total: 0,
		googleTotal: 0,
		googleAuth: 0,
		appleTotal: 0,
		appleAuth: 0,
		emailTotal: 0,
		emailAuth: 0,
		emailOtp: 0,
		emailPassword: 0,
	});

	useEffect(() => {
		const load = async () => {
			try {
				setIsLoading(true)
				const data = await getSignupFunnel(filter);
				// data is array of rows per SignUpType
				const byType = {
					google: data.find((d) => d.SignUpType === "google") || {},
					apple: data.find((d) => d.SignUpType === "apple") || {},
					email: data.find((d) => d.SignUpType === "email") || {},
				};

				const total =
					(byType.google.TotalSignUps || 0) +
					(byType.apple.TotalSignUps || 0) +
					(byType.email.TotalSignUps || 0);

				setTotals({
					total,
					googleTotal: byType.google.TotalSignUps || 0,
					googleAuth: byType.google.isCompletedCount || 0,
					appleTotal: byType.apple.TotalSignUps || 0,
					appleAuth: byType.apple.isCompletedCount || 0,
					emailTotal: byType.email.TotalSignUps || 0,
					emailAuth: byType.email.isCompletedCount || 0,
					emailOtp: byType.email.verifiedEmailOtpCount || 0,
					emailPassword: byType.email.emailPasswordCreatedCount || 0,
				});
			} catch (err) {
				console.error("Error loading signup funnel", err);
			}
			setIsLoading(false)
		};

		load();

	}, [JSON.stringify(filterWithoutUTM)]);

	return (
		<Box
			sx={{
				border: "1px solid #E8E8E8",
				p: 2,
				borderRadius: "12px",
				background: "#fff",
			}}
		>
			<Typography
				sx={{
					fontSize: "18px",
					fontWeight: 500,
					color: "#101828",
					mb: 0,
					borderLeft: "3px solid #FBBF24",
					pl: 1,
				}}
			>
				User Flow Breakdown
			</Typography>
			<Typography
				sx={{
					fontSize: "12px",
					color: "#666666",
					mb: 3,
					pt: 1,
				}}
			>
				Detailed metrics for each signup step
			</Typography>

			{isLoading ? <Box sx={{ textAlign: 'center', py: 4 }}>
				<Typography variant="body1" color="text.secondary">
					Loading...
				</Typography>
			</Box> : <Box>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "stretch",
						gap: 2,
					}}
				>
					{/* TOTAL */}
					<Box
						sx={{
							background: "#F5F3F0",
							p: 2,
							borderRadius: 2,
							width: "100%",
							maxWidth: "200px",
						}}
					>
						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: "repeat(2, 1fr)",
								pb: 2,
								mb: 2,
							}}
						>
							<Typography sx={{ fontSize: "24px", color: "#0A0A0A" }}>
								<Typography
									sx={{
										display: "block",
										fontSize: "12px",
										color: "#6B6B6B",
									}}
								>
									Total
								</Typography>
								{totals.total.toLocaleString()}
							</Typography>
						</Box>
					</Box>

					{/* GOOGLE / APPLE */}
					<Box sx={{ width: "100%" }}>
						<Box
							sx={{
								background: "#F5F3F0",
								p: 2,
								borderRadius: 2,
								width: "100%",
							}}
						>
							<Box
								sx={{
									borderBottom: "1px solid #E5E1DC",
									display: "grid",
									gridTemplateColumns: "repeat(2, 1fr)",
									pb: 2,
									mb: 2,
								}}
							>
								<Typography sx={{ fontSize: "24px", color: "#0A0A0A" }}>
									<Typography
										sx={{
											display: "block",
											fontSize: "12px",
											color: "#6B6B6B",
										}}
									>
										Google
									</Typography>
									{totals.googleTotal.toLocaleString()}
								</Typography>
								<Typography sx={{ fontSize: "24px", color: "#0A0A0A" }}>
									<Typography
										sx={{
											display: "block",
											fontSize: "12px",
											color: "#6B6B6B",
										}}
									>
										Apple
									</Typography>
									{totals.appleTotal.toLocaleString()}
								</Typography>
							</Box>
							<Box
								sx={{
									display: "grid",
									gridTemplateColumns: "repeat(2, 1fr)",
								}}
							>
								<Typography sx={{ fontSize: "20px", color: "#0A0A0A" }}>
									<Typography
										sx={{
											display: "block",
											fontSize: "12px",
											color: "#6B6B6B",
										}}
									>
										Google 100% profile completed
									</Typography>
									{totals.googleAuth.toLocaleString()}
								</Typography>
								<Typography sx={{ fontSize: "20px", color: "#0A0A0A" }}>
									<Typography
										sx={{
											display: "block",
											fontSize: "12px",
											color: "#6B6B6B",
										}}
									>
										Apple 100% profile completed
									</Typography>
									{totals.appleAuth.toLocaleString()}
								</Typography>
							</Box>
						</Box>
					</Box>

					{/* EMAIL BLOCK */}
					<Box sx={{ width: "100%" }}>
						<Box
							sx={{
								background: "#F5F3F0",
								p: 2,
								borderRadius: 2,
								width: "100%",
							}}
						>
							<Box
								sx={{
									borderBottom: "1px solid #E5E1DC",
									display: "grid",
									gridTemplateColumns: "repeat(2, 1fr)",
									pb: 2,
									mb: 2,
								}}
							>
								<Typography sx={{ fontSize: "24px", color: "#0A0A0A" }}>
									<Typography
										sx={{
											display: "block",
											fontSize: "12px",
											color: "#6B6B6B",
										}}
									>
										Email
									</Typography>
									{totals.emailTotal.toLocaleString()}
								</Typography>
							</Box>
							<Box>
								<Typography
									sx={{
										fontSize: "16px",
										color: "#0A0A0A",
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
									}}
								>
									<Typography
										sx={{
											display: "block",
											fontSize: "12px",
											color: "#6B6B6B",
										}}
									>
										Email 100% profile completed
									</Typography>
									{totals.emailAuth.toLocaleString()}
								</Typography>
								<Typography
									sx={{
										fontSize: "16px",
										color: "#0A0A0A",
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
									}}
								>
									<Typography
										sx={{
											display: "block",
											fontSize: "12px",
											color: "#6B6B6B",
										}}
									>
										Verify Email (OTP)
									</Typography>
									{totals.emailOtp.toLocaleString()}
								</Typography>
								<Typography
									sx={{
										fontSize: "16px",
										color: "#0A0A0A",
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
									}}
								>
									<Typography
										sx={{
											display: "block",
											fontSize: "12px",
											color: "#6B6B6B",
										}}
									>
										Create Password
									</Typography>
									{totals.emailPassword.toLocaleString()}
								</Typography>
							</Box>
						</Box>
					</Box>
				</Box>
			</Box>}
		</Box>
	);
};

export default UserFlowBreakdown;
